<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    @input="handleInput"
  >
    <v-card
      tile
      class="d-flex flex-column wizard-card"
    >
      <v-card-text class="wizard-body flex-grow-1 pa-0">
        <!-- ステッパーを本文スクロール領域内の sticky 要素にして、v-tabs の scrollIntoView 等で押し出されないようにする -->
        <wizard-stepper
          class="wizard-stepper-sticky"
          :current-step="currentStep"
          :step-names="stepNames"
          :next-disabled="nextDisabled"
          @navigate="goToStep"
        />

        <!-- Step: 席種・客数・税サ -->
        <init-config-course-step
          v-if="currentStepKey === 'init'"
          :courses="courses"
          :selected-course-id="formState.selectedCourseId"
          :customer-count="formState.customerCount"
          :tax-charge="formState.taxCharge"
          @update:selectedCourseId="formState.selectedCourseId = $event"
          @update:customerCount="formState.customerCount = $event"
          @update:taxCharge="handleTaxChargeChange"
        />

        <!-- Step: お客様情報 (指名/リファラル主軸 + 顧客は卓全体に紐付け) -->
        <customer-info-step
          v-else-if="currentStepKey === 'customer'"
          :nominations="nominations"
          :users="users"
          :referrals="referrals"
          :selected-customer-ids="formState.selectedCustomerIds"
          :cast-nominations="formState.castNominations"
          :referral-rows-value="formState.referralRows"
          @update:selectedCustomerIds="formState.selectedCustomerIds = $event"
          @update:castNominations="formState.castNominations = $event"
          @update:referralRowsValue="formState.referralRows = $event"
        />

        <!-- Step: チャージ (items が無い店舗ではスキップ) -->
        <charge-step
          v-else-if="currentStepKey === 'charge'"
          :items="items"
          :charges="formState.charges"
          @update:charges="formState.charges = $event"
        />

        <!-- Step: セット時間/料金確認 -->
        <set-confirm-step
          v-else-if="currentStepKey === 'confirm'"
          :customer-count="formState.customerCount"
          :selected-course="selectedCourse"
          :set="formState.set"
          :tax-charge="formState.taxCharge"
          :start-at="formState.startAt"
          @update:set="formState.set = $event"
          @update:taxCharge="handleTaxChargeChange"
          @update:startAt="formState.startAt = $event"
        />
      </v-card-text>

      <wizard-footer
        class="wizard-footer-fixed"
        :current-step="currentStep"
        :total-steps="totalSteps"
        :next-disabled="nextDisabled"
        :submitting="isSubmitting"
        @prev="goPrev"
        @next="goNext"
        @complete="handleComplete"
        @save-draft="handleSaveDraft"
        @cancel="handleCancel"
      />
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* Wizard 全体: viewport 高さに固定し、stepper と footer を画面端に固定、本文だけスクロール */
.wizard-card {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
}
.wizard-body {
  overflow-y: auto;
  /* footer の高さ分の余白を確保（footer 自体は固定なので本文末がボタンに隠れないように） */
  padding-bottom: 112px;
}
.wizard-stepper-sticky {
  position: sticky;
  top: 0;
  z-index: 4;
  background: var(--v-background-base, #fff);
}
.wizard-footer-fixed {
  position: sticky;
  bottom: 0;
  z-index: 5;
}
</style>

<script>
import {
  ref, reactive, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import TableApi from '@/api/waiter/Table'
import { buildTableCreatePayload } from '@/views/composable/useTableCreatePayload'
import WizardStepper from './WizardStepper.vue'
import WizardFooter from './WizardFooter.vue'
import InitConfigCourseStep from './steps/InitConfigCourseStep.vue'
import CustomerInfoStep from './steps/CustomerInfoStep.vue'
import ChargeStep from './steps/ChargeStep.vue'
import SetConfirmStep from './steps/SetConfirmStep.vue'

export default {
  name: 'TableCreateWizard',
  components: {
    WizardStepper,
    WizardFooter,
    InitConfigCourseStep,
    CustomerInfoStep,
    ChargeStep,
    SetConfirmStep,
  },
  props: {
    value: { type: Boolean, default: false },
    hallId: { type: [Number, String], required: true },
    tableNumber: { type: [Number, String], required: true },
    tablePrefix: { type: String, default: '' },
    tableSuffix: { type: String, default: '卓' },
    courses: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    nominations: { type: Array, default: () => [] },
    referrals: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    initialCustomerIds: { type: Array, default: () => [] },
  },
  emits: ['input', 'close', 'created'],
  setup(props, { emit }) {
    const currentStep = ref(1)
    const hasCharges = computed(() => Array.isArray(props.items) && props.items.length > 0)
    const stepKeys = computed(() => {
      const keys = ['init', 'customer']
      if (hasCharges.value) keys.push('charge')
      keys.push('confirm')

      return keys
    })
    const stepNames = computed(() => stepKeys.value.map(k => ({
      init: '席種・客数',
      customer: 'お客様情報',
      charge: 'チャージ',
      confirm: 'セット確認',
    }[k])))
    const totalSteps = computed(() => stepKeys.value.length)
    const currentStepKey = computed(() => stepKeys.value[currentStep.value - 1])

    // 現在時刻を 5 分単位で切り上げ (例: 11:56 → 12:00)
    const ceilToFiveMinutes = (date = new Date()) => {
      const d = new Date(date.getTime())
      const m = d.getMinutes()
      d.setMinutes(Math.ceil(m / 5) * 5, 0, 0)

      return d
    }

    const formState = reactive({
      // Step 1
      selectedCourseId: null,
      customerCount: 0,
      taxCharge: 'normal', // 'normal' | 'cut' - Step1 で初期選択、Step4 で上書き可
      // Step 2 (指名/リファラル主軸 + 顧客は卓全体に紐付け)
      selectedCustomerIds: [],
      castNominations: [], // [{ castId, vendibleId, sellingPrice }]
      referralRows: [], // [{ referralId, userId }]
      // Step 3
      charges: [],
      // Step 4: セット開始時間 (Date)
      startAt: ceilToFiveMinutes(),
      // Step 4
      set: {
        mode: 'bulk',
        durationMin: 0,
        price: 0,
        perCustomer: [],
      },
    })

    const handleTaxChargeChange = v => {
      formState.taxCharge = v || 'normal'
      // 個別モード時は perCustomer 各行の taxCharge も追従
      if (formState.set.mode === 'individual' && Array.isArray(formState.set.perCustomer)) {
        formState.set.perCustomer = formState.set.perCustomer.map(r => ({
          ...r,
          taxCharge: formState.taxCharge,
        }))
      }
    }

    // Step 1 で選択された Course オブジェクト
    const selectedCourse = computed(() => props.courses
      .find(c => c.id === formState.selectedCourseId) || null)

    // 選択 Course が変わったら set のデフォルト値を更新
    const courseDurationMin = c => {
      if (!c) return 0
      if (c.effectiveDurationSeconds != null) return Math.round(c.effectiveDurationSeconds / 60)
      if (c.servingTimeMin != null) return c.servingTimeMin
      if (c.durationMin != null) return c.durationMin

      return 0
    }
    watch(selectedCourse, c => {
      if (c) {
        formState.set = {
          ...formState.set,
          durationMin: courseDurationMin(c),
          price: c.sellingPrice || 0,
        }
      }
    })

    // 後段の payload 組立で使う「customers」を派生
    // 形式: [{ customerId, nominations: [], referrals: [] }, ...]
    // 旧 formState.customers の役割は selectedCustomerIds + castNominations + referralRows に分散
    const derivedCustomers = computed(() => {
      const ids = formState.selectedCustomerIds || []
      if (ids.length === 0) {
        // 顧客未選択: 卓全体として 1 件の匿名 customer を作る
        return [{
          customerId: null,
          nominations: formState.castNominations || [],
          referrals: formState.referralRows || [],
        }]
      }

      // 顧客が選択されている場合: 1 件目に全紐付けを集約（指名/リファラルは卓全体のため）
      return ids.map((id, idx) => ({
        customerId: id,
        nominations: idx === 0 ? (formState.castNominations || []) : [],
        referrals: idx === 0 ? (formState.referralRows || []) : [],
      }))
    })

    // 各ステップの「次へ」活性条件（Phase 3〜6 で詳細化）
    const nextDisabled = computed(() => {
      switch (currentStep.value) {
        case 1:
          return !formState.selectedCourseId || formState.customerCount < 1
        default:
          return false
      }
    })

    const goNext = () => {
      if (currentStep.value < totalSteps.value) {
        currentStep.value += 1
      }
    }

    const goPrev = () => {
      if (currentStep.value > 1) {
        currentStep.value -= 1
      }
    }

    // stepper クリックで任意のステップにジャンプ
    // 通過済みステップは常に / 先のステップは現在ステップが valid な時だけ
    const goToStep = step => {
      if (step < 1 || step > totalSteps.value) return
      if (step <= currentStep.value) {
        currentStep.value = step

        return
      }
      if (!nextDisabled.value) {
        currentStep.value = step
      }
    }

    const isSubmitting = ref(false)
    const vm = getCurrentInstance()?.proxy

    // 卓作成 API 呼び出し
    const submitCreateTable = async () => {
      if (isSubmitting.value) return
      isSubmitting.value = true
      try {
        const payload = buildTableCreatePayload({
          startAt: formState.startAt,
          hallId: props.hallId,
          tableNumber: props.tableNumber,
          formState: {
            ...formState,
            customers: derivedCustomers.value,
          },
          selectedCourse: selectedCourse.value,
        })
        const res = await TableApi.createTable(payload)
        const table = res?.data?.data || null
        emit('created', table)
        if (vm?.$toast?.success) {
          vm.$toast.success(`${[props.tablePrefix, props.tableNumber, props.tableSuffix].join('')}を作成しました`)
        }
        emit('input', false)
        emit('close')
      } catch (e) {
        if (vm?.$toast?.error) {
          vm.$toast.error('卓の作成に失敗しました')
        }
        // エラー時はウィザードを閉じない（再試行可）
      } finally {
        isSubmitting.value = false
      }
    }

    const handleComplete = async () => {
      await submitCreateTable()
    }

    // 「一旦作成」: 現時点の入力でそのまま卓を作成しウィザードを閉じる
    const handleSaveDraft = async () => {
      await submitCreateTable()
    }

    const handleCancel = () => {
      // TODO: 入力済みなら確認ダイアログ
      emit('input', false)
      emit('close')
    }

    const handleInput = v => {
      emit('input', v)
    }

    return {
      currentStep,
      currentStepKey,
      totalSteps,
      stepNames,
      formState,
      selectedCourse,
      derivedCustomers,
      isSubmitting,
      nextDisabled,
      goNext,
      goPrev,
      goToStep,
      submitCreateTable,
      handleComplete,
      handleSaveDraft,
      handleCancel,
      handleInput,
      handleTaxChargeChange,
    }
  },
}
</script>
